import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useHistory } from "react-router-dom";
import { useAuthToken, LOGIN_LOGGED_AT } from './useAuthToken';
import { useLoginMutation } from './useLoginMutation';
import { useRevalidateMutation } from '@api/graphql-codegen';
import { IDLE_TIME_THROTTLE, SA_IDLE_TIME_THROTTLE } from '@src/constants/activity';
import { shouldSkipActivityTracking } from '@src/utils/activityUtils';
import { useUserSettings } from './useUserSettings';
import { logger } from '../utils/logger';

// Constants for storage keys
const ALL_TABS_ACTIVITY_STATE = 'op-all-tabs-activity-state'; // Key for storing all tabs' activity states - use op- prefix to avoid conflicts

const oneHr = 60 * 60 * 1000;
export const useAuthTokenRevalidation = () => {
  const history = useHistory();
  const { token, setAuthToken } = useAuthToken();
  const [, logout] = useLoginMutation();
  const init = useRef(true);
  let countDownTimer = useRef<NodeJS.Timeout | null>(null);
  // Add a ref to track if revalidation is in progress
  const revalidationInProgress = useRef<boolean>(false);

  const [revalidate] = useRevalidateMutation({
    onError(err: any) {
      const errorMessage = err?.message || err?.graphQLErrors?.[0]?.message;
      logger.warn('[Token Revalidation] Error', {
        errorMessage,
        errorType: err?.name,
        graphQLErrors: err?.graphQLErrors,
        networkError: err?.networkError,
        timestamp: new Date().toISOString()
      });
      
      // Check if session has timed out before handling error
      const sessionTimedOut = localStorage.getItem('sessionTimeOut');
      if (sessionTimedOut) {
        logger.warn('[Token Revalidation] Ignoring error due to timeout', {
          timeoutSetAt: new Date(Number(sessionTimedOut)).toISOString(),
          timestamp: new Date().toISOString()
        });
        return;
      }
      
      if (errorMessage === 'InvalidToken!' || errorMessage === 'Response not successful: Received status code 500' || errorMessage === 'UserNotFound!') {
        logger.warn('[Token Revalidation] Logging out due to error', {
          errorMessage,
          timestamp: new Date().toISOString()
        });
        logout();
        history.replace("/login");
      }
    },
    onCompleted(res) {
      const newToken = res.revalidateToken;
      logger.warn('[Token Revalidation] Completed', {
        hasNewToken: !!newToken?.token,
        hasClientId: !!newToken?.clientId,
        hasEmail: !!newToken?.email,
        timestamp: new Date().toISOString()
      });

      // Check if session has timed out before setting new token
      const sessionTimedOut = localStorage.getItem('sessionTimeOut');
      if (sessionTimedOut) {
        logger.warn('[Token Revalidation] Ignoring revalidation result due to timeout', {
          timeoutSetAt: new Date(Number(sessionTimedOut)).toISOString(),
          timestamp: new Date().toISOString()
        });
        return;
      }

      if (newToken?.clientId) {
        localStorage.setItem('clientId', newToken?.clientId)
      }

      if (newToken?.token) {
        const lastLoginAt = localStorage.getItem(LOGIN_LOGGED_AT);
        const timeSinceLogin = lastLoginAt ? Date.now() - Number(lastLoginAt) : 0;
        logger.warn('[Token Revalidation] Setting token', {
          hasLastLogin: !!lastLoginAt,
          timeSinceLogin: timeSinceLogin || 0,
          willLogTime: (!lastLoginAt || timeSinceLogin > 60 * 1000) && !!newToken?.email,
          timestamp: new Date().toISOString()
        });

        if ((!lastLoginAt || Date.now() > (Number(lastLoginAt) + (60 * 1000))) && newToken?.email) {
          setAuthToken(newToken.token, true);
        } else {
          setAuthToken(newToken.token);
        }
      } else if (token) {
        logger.warn('[Token Revalidation] No new token, logging out', {
          timestamp: new Date().toISOString()
        });
        logout();
      }
    },
  });

  // Get user settings
  const { autoLogoutEnabled } = useUserSettings();
  
  // Check if the current URL should skip activity tracking or auto logout is disabled
  const skipActivityTracking = useMemo(() => 
    shouldSkipActivityTracking() || !autoLogoutEnabled, 
    [autoLogoutEnabled]
  );

  // Function to check user idle state from localStorage
  const getUserIdleState = useCallback(() => {
    let allTabsState: Record<string, any> = {};
    let idleState = 'Active';
    let idleTimeSeconds = 0;
    let isSuperAdmin = false;
    
    try {
      const storedState = localStorage.getItem(ALL_TABS_ACTIVITY_STATE);
      if (storedState) {
        allTabsState = JSON.parse(storedState);
        
        // Log the tab IDs for debugging
        if (import.meta.env.DEV) {
          logger.debug('[Token Revalidation] All tabs activity state:', {
            tabIds: Object.keys(allTabsState),
            timestamp: new Date().toISOString()
          });
        }
        
        // Find the most idle time across all tabs
        Object.values(allTabsState).forEach((tabState: any) => {
          if (tabState.state === 'Idle' && tabState.idleTimeSeconds > idleTimeSeconds) {
            idleState = 'Idle';
            idleTimeSeconds = tabState.idleTimeSeconds;
          }
          if (tabState.isSuperAdmin) {
            isSuperAdmin = true;
          }
        });
      }
    } catch (parseError) {
      logger.error('[Token Revalidation] Error parsing all tabs state:', parseError);
    }
    
    return { idleState, idleTimeSeconds, isSuperAdmin, allTabsState };
  }, []);
  
  // Function to perform token revalidation
  const performRevalidation = useCallback((currentToken: string) => {
    const variables: any = { 
      clientId: localStorage.getItem('clientId') || undefined, 
      token: currentToken
    };
    
    logger.warn('[Token Revalidation] Attempting revalidation', {
      clientId: variables.clientId,
      tokenFirstChars: currentToken.substring(0, 10) + '...',
      timestamp: new Date().toISOString()
    });
    
    revalidate({ variables: { input: variables } });
  }, [revalidate]);
  
  // Function to handle scheduled revalidation
  const handleScheduledRevalidation = useCallback(() => {
    try {
      // Check if we should skip activity tracking (URL might have changed)
      // or if auto logout is disabled
      const autoLogoutEnabled = localStorage.getItem('autoLogoutEnabled') !== 'false';
      if (shouldSkipActivityTracking() || !autoLogoutEnabled) {
        logger.warn('[Token Revalidation] Skipping scheduled revalidation', {
          reason: !autoLogoutEnabled ? 'auto logout disabled' : 'non-tracked URL',
          timestamp: new Date().toISOString()
        });
        return;
      }
      
      // Check if session has timed out before revalidating
      const currentSessionTimedOut = localStorage.getItem('sessionTimeOut');
      if (currentSessionTimedOut) {
        logger.warn('[Token Revalidation] Skipping scheduled revalidation due to timeout', {
          timeoutSetAt: new Date(Number(currentSessionTimedOut)).toISOString(),
          timestamp: new Date().toISOString()
        });
        return;
      }
      
      // Check if we're still logged in before attempting revalidation
      const currentToken = localStorage.getItem('authToken');
      if (!currentToken) {
        logger.warn('[Token Revalidation] No token found in scheduled revalidation - skipping');
        return;
      }
      
      // Check if user is currently idle
      const { idleState, idleTimeSeconds, isSuperAdmin } = getUserIdleState();
      
      if (idleState === 'Idle' && idleTimeSeconds > 0) {
        const idleThreshold = isSuperAdmin ? SA_IDLE_TIME_THROTTLE : IDLE_TIME_THROTTLE;
        const revalidationSkipThreshold = idleThreshold - 1; // 1 second before timeout (same as activity modal)
        
        logger.warn('[Token Revalidation] User is idle, checking idle time', {
          idleTimeSeconds,
          idleThreshold,
          revalidationSkipThreshold,
          isSuperAdmin,
          timestamp: new Date().toISOString()
        });
        
        // If user has been idle for too long, skip revalidation
        // This is less than the idle timeout to prevent race conditions
        if (idleTimeSeconds >= revalidationSkipThreshold) {
          logger.warn('[Token Revalidation] User has been idle too long, skipping revalidation', {
            idleTimeSeconds,
            revalidationSkipThreshold,
            timestamp: new Date().toISOString()
          });
          return;
        }
      }
      
      logger.warn('[Token Revalidation] Performing scheduled revalidation', {
        timestamp: new Date().toISOString()
      });
      
      performRevalidation(currentToken);
    } catch (error) {
      logger.error('[Token Revalidation] Error in scheduled revalidation:', error);
    }
  }, [getUserIdleState, performRevalidation]);
  
  // Initial setup and token revalidation
  useEffect(() => {
    if (init.current) {
      try {
        // TEMPORARY: Disable token revalidation to stop re-render loop
        logger.warn('[Token Revalidation] Temporarily disabled to prevent re-render loop');
        init.current = false;
        return;
        
        /* COMMENTED OUT - Original token revalidation logic
        // Don't revalidate if we were logged out due to session timeout
        // or if we're on a page that should skip activity tracking
        const sessionTimedOut = localStorage.getItem('sessionTimeOut');
        
        // Skip token revalidation for pages that should skip activity tracking
        if (skipActivityTracking) {
          init.current = false;
          return;
        }
        
        // If session timed out, clear the flag but don't revalidate in this session
        if (sessionTimedOut) {
          logger.warn('[Token Revalidation] Session timed out, skipping revalidation', {
            timeoutSetAt: new Date(Number(sessionTimedOut)).toISOString(),
            timeElapsed: Date.now() - Number(sessionTimedOut),
            timestamp: new Date().toISOString()
          });
          
          // Clear the flag for future sessions
          localStorage.removeItem('sessionTimeOut');
          
          // Verify the flag was cleared
          const verifyCleared = localStorage.getItem('sessionTimeOut');
          logger.warn('[Token Revalidation] Session timeout flag cleared', {
            success: verifyCleared === null,
            timestamp: new Date().toISOString()
          });
          
          // Skip revalidation for this session
          init.current = false;
          return;
        }
        
        if (token && !sessionTimedOut) {
          // Check if user is currently idle before initial revalidation
          const { idleState, idleTimeSeconds, isSuperAdmin } = getUserIdleState();
          
          if (idleState === 'Idle' && idleTimeSeconds > 0) {
            const idleThreshold = isSuperAdmin ? SA_IDLE_TIME_THROTTLE : IDLE_TIME_THROTTLE;
            const revalidationSkipThreshold = idleThreshold - 1; // 1 second before timeout
            
            logger.warn('[Token Revalidation] Initial check - user is idle', {
              idleTimeSeconds,
              idleThreshold,
              revalidationSkipThreshold,
              isSuperAdmin,
              timestamp: new Date().toISOString()
            });
            
            // If user has been idle for too long, skip initial revalidation
            if (idleTimeSeconds >= revalidationSkipThreshold) {
              logger.warn('[Token Revalidation] Initial check - user has been idle too long, skipping revalidation', {
                idleTimeSeconds,
                revalidationSkipThreshold,
                timestamp: new Date().toISOString()
              });
              
              // Skip revalidation but still set up the timer for future checks
              init.current = false;
              
              // Set up the periodic revalidation (continue with the timer setup below)
              // but don't perform the initial revalidation
            } else {
              // User is idle but not for too long, proceed with revalidation
              const variables: any = { clientId: localStorage.getItem('clientId') || undefined, token }
              logger.warn('[Token Revalidation] Attempting revalidation', {
                clientId: variables.clientId,
                tokenFirstChars: token.substring(0, 10) + '...',
                timestamp: new Date().toISOString()
              });
              
              // Check if revalidation is already in progress
              if (!revalidationInProgress.current) {
                // Set the flag to indicate revalidation is in progress
                revalidationInProgress.current = true;
                
                // Execute the revalidation
                revalidate({ variables: { input: variables } })
                  .finally(() => {
                    // Reset the flag when revalidation is complete
                    revalidationInProgress.current = false;
                  });
              } else {
                logger.warn('[Token Revalidation] Revalidation already in progress, skipping', {
                  timestamp: new Date().toISOString()
                });
              }
            }
          } else {
            // User is not idle, proceed with normal revalidation
            const variables: any = { clientId: localStorage.getItem('clientId') || undefined, token }
            logger.warn('[Token Revalidation] Attempting revalidation', {
              clientId: variables.clientId,
              tokenFirstChars: token.substring(0, 10) + '...',
              timestamp: new Date().toISOString()
            });
            
            // Check if revalidation is already in progress
            if (!revalidationInProgress.current) {
              // Set the flag to indicate revalidation is in progress
              revalidationInProgress.current = true;
              
              // Execute the revalidation
              revalidate({ variables: { input: variables } })
                .finally(() => {
                  // Reset the flag when revalidation is complete
                  revalidationInProgress.current = false;
                });
            } else {
              logger.warn('[Token Revalidation] Revalidation already in progress, skipping', {
                timestamp: new Date().toISOString()
              });
            }
          }
          
          // Set up the periodic revalidation
          if (countDownTimer.current) {
            clearInterval(countDownTimer.current);
          }
          
          countDownTimer.current = setInterval(() => {
            try {
              // Check if we should skip activity tracking (URL might have changed)
              // or if auto logout is disabled
              const autoLogoutEnabled = localStorage.getItem('autoLogoutEnabled') !== 'false';
              if (shouldSkipActivityTracking() || !autoLogoutEnabled) {
                logger.warn('[Token Revalidation] Skipping scheduled revalidation', {
                  reason: !autoLogoutEnabled ? 'auto logout disabled' : 'non-tracked URL',
                  timestamp: new Date().toISOString()
                });
                return;
              }
              
              // Check if session has timed out before revalidating
              const currentSessionTimedOut = localStorage.getItem('sessionTimeOut');
              if (currentSessionTimedOut) {
                logger.warn('[Token Revalidation] Skipping scheduled revalidation due to timeout', {
                  timeoutSetAt: new Date(Number(currentSessionTimedOut)).toISOString(),
                  timestamp: new Date().toISOString()
                });
                return;
              }
              
              // Check if we're still logged in before attempting revalidation
              const currentToken = localStorage.getItem('authToken');
              if (!currentToken) {
                logger.warn('[Token Revalidation] No token found in scheduled revalidation - skipping');
                return;
              }
              
              // Check if user is currently idle
              const idleState = localStorage.getItem('userIdleState');
              const idleTime = localStorage.getItem('userIdleTime');
              const isSuperAdmin = localStorage.getItem('userIsSuperAdmin') === 'true';
              
              if (idleState === 'Idle' && idleTime) {
                const idleTimeSeconds = parseInt(idleTime, 10);
                const idleThreshold = isSuperAdmin ? SA_IDLE_TIME_THROTTLE : IDLE_TIME_THROTTLE;
                const revalidationSkipThreshold = idleThreshold - 1; // 1 second before timeout (same as activity modal)
                
                logger.warn('[Token Revalidation] User is idle, checking idle time', {
                  idleTimeSeconds,
                  idleThreshold,
                  revalidationSkipThreshold,
                  isSuperAdmin,
                  timestamp: new Date().toISOString()
                });
                
                // If user has been idle for too long, skip revalidation
                // This is less than the idle timeout to prevent race conditions
                if (idleTimeSeconds >= revalidationSkipThreshold) {
                  logger.warn('[Token Revalidation] User has been idle too long, skipping revalidation', {
                    idleTimeSeconds,
                    revalidationSkipThreshold,
                    timestamp: new Date().toISOString()
                  });
                  return;
                }
              }
              
              const variables: any = { 
                clientId: localStorage.getItem('clientId') || undefined, 
                token: currentToken
              }
              
              logger.warn('[Token Revalidation] Performing scheduled revalidation', {
                timestamp: new Date().toISOString()
              });
              
              // Check if revalidation is already in progress
              if (!revalidationInProgress.current) {
                // Set the flag to indicate revalidation is in progress
                revalidationInProgress.current = true;
                
                // Execute the revalidation
                revalidate({ variables: { input: variables } })
                  .finally(() => {
                    // Reset the flag when revalidation is complete
                    revalidationInProgress.current = false;
                  });
              } else {
                logger.warn('[Token Revalidation] Revalidation already in progress, skipping', {
                  timestamp: new Date().toISOString()
                });
              }
            } catch (error) {
              logger.error('[Token Revalidation] Error in scheduled revalidation:', error);
            }
          }, oneHr);
        }
        */
      } catch (error) {
        logger.error('[Token Revalidation] Error during initialization:', error);
      }
    }
    init.current = false;
  }, [revalidate, token, skipActivityTracking]);

  useEffect(() => {
    return () => {
      if (countDownTimer.current) {
        clearInterval(countDownTimer.current)
        countDownTimer.current = null;
      }
    }
  }, [])

  return null;
}
